import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useGames } from '../../context/GameContext';
import { useButtonHealthCheck } from '../../hooks/useButtonHealthCheck';
import { safeNavigate, getNavigationHistory, clearNavigationHistory } from '../../utils/navigationUtils';
import './Layout.css';

// Enhanced Protected Link component with smart navigation
const ProtectedLink = ({ to, children, className, ...props }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { checkUnsavedChanges } = useGames();
  const isNavigatingRef = useRef(false);
  const lastClickTimeRef = useRef(0);

  const executeNavigation = useCallback(() => {
    // Use safe navigation to prevent duplicates
    const success = safeNavigate(navigate, to);
    
    if (success) {
      // Update our local navigation flag
      isNavigatingRef.current = true;
      
      // Reset flag after navigation
      setTimeout(() => {
        isNavigatingRef.current = false;
      }, 500);
    }
  }, [navigate, to]);

  const handleClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const now = Date.now();
    // Prevent rapid successive clicks
    if (now - lastClickTimeRef.current < 300) {
      console.warn('🚫 Rapid click detected, ignoring');
      return;
    }
    lastClickTimeRef.current = now;



    // Primary attempt: try checkUnsavedChanges
    try {
      if (checkUnsavedChanges && typeof checkUnsavedChanges === 'function') {
        checkUnsavedChanges(() => {
          executeNavigation();
        });
      } else {
        // Fallback: direct navigation if checkUnsavedChanges is not available
        executeNavigation();
      }
    } catch (error) {
      console.error('Error in checkUnsavedChanges:', error);
      // Fallback: direct navigation on error
      executeNavigation();
    }
  }, [checkUnsavedChanges, executeNavigation]);



  // Reset navigation flag when location changes
  useEffect(() => {
    isNavigatingRef.current = false;
  }, [location.pathname]);

  return (
    <a href={to} onClick={handleClick} className={className} {...props}>
      {children}
    </a>
  );
};

// Enhanced Button component with retry mechanism
const EnhancedButton = ({ onClick, children, className, ...props }) => {
  const retryTimeoutRef = useRef(null);
  const clickCountRef = useRef(0);
  const lastClickTimeRef = useRef(0);

  const handleClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const now = Date.now();
    // Prevent double clicks within 300ms
    if (now - lastClickTimeRef.current < 300) {
      return;
    }
    lastClickTimeRef.current = now;
    
    clickCountRef.current += 1;
    const currentClickCount = clickCountRef.current;
    
    // Clear any existing retry timeout
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
    }

    // Primary attempt
    try {
      if (onClick && typeof onClick === 'function') {
        const result = onClick(e);
        
        // If onClick returns a promise (async function), handle it
        if (result && typeof result.then === 'function') {
          result.catch((error) => {
            console.error('Error in async button click:', error);
            // Retry after a short delay for async operations
            retryTimeoutRef.current = setTimeout(() => {
              if (clickCountRef.current === currentClickCount) {
                console.warn('🔄 Retrying async button click');
                onClick(e);
              }
            }, 1000);
          });
        }
      }
    } catch (error) {
      console.error('Error in button click handler:', error);
      // Retry after error
      retryTimeoutRef.current = setTimeout(() => {
        if (clickCountRef.current === currentClickCount) {
          console.warn('🔄 Retrying button click after error');
          try {
            onClick(e);
          } catch (retryError) {
            console.error('Retry also failed:', retryError);
          }
        }
      }, 1000);
    }
  }, [onClick]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      const timeoutId = retryTimeoutRef.current;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  return (
    <button onClick={handleClick} className={className} {...props}>
      {children}
    </button>
  );
};

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const authContext = useAuth();
  const { currentUser, logout } = authContext || {};
  const [lastGameId, setLastGameId] = useState(null);
  
  // מפעיל את מנגנון בדיקת בריאות הכפתורים
  const { manualHealthCheck } = useButtonHealthCheck();

  // Effect לעקוב אחרי gameId ולשמור אותו ב-localStorage
  useEffect(() => {
    // רק אם אנחנו בעמוד העורך
    if (location.pathname === '/app' || location.pathname === '/') {
      const gameId = searchParams.get('gameId');
      if (gameId && gameId !== 'new') {
        // שמירה ב-localStorage
        localStorage.setItem('dnd_last_game_id', gameId);
        setLastGameId(gameId);
      } else if (!gameId) {
        // אם אין gameId (משחק חדש), נוקה את השמור ונחזור לקישור בסיסי
        localStorage.removeItem('dnd_last_game_id');
        setLastGameId(null);
      }
    }
  }, [location.pathname, searchParams]);

  // Effect לטעון את ה-gameId השמור בעת טעינת הקומפוננט
  useEffect(() => {
    const savedGameId = localStorage.getItem('dnd_last_game_id');
    if (savedGameId) {
      setLastGameId(savedGameId);
    }
  }, []);

  // Effect לעקוב אחרי שינויים במיקום ולעדכן את lastGameId בהתאם
  useEffect(() => {
    // אם אנחנו במשחק חדש, נוודא שה-lastGameId מתאפס
    if (location.pathname === '/map-editor/new') {
      setLastGameId(null);
    }
  }, [location.pathname]);

  // בניית הקישור לעורך המפות
  const getMapEditorLink = () => {
    // אם אנחנו כבר בעורך המפות
    if (location.pathname === '/app' || location.pathname === '/' || location.pathname === '/map-editor/new') {
      const currentGameId = searchParams.get('gameId');
      // אם יש gameId נוכחי ושונה מ-'new', נשמור אותו
      if (currentGameId && currentGameId !== 'new') {
        return `/app?gameId=${currentGameId}`;
      }
      // אם אנחנו במשחק חדש, נחזור לקישור בסיסי
      return '/app';
    }
    
    // אם יש gameId שמור ואנחנו לא במשחק חדש, משתמשים בו
    if (lastGameId && location.pathname !== '/map-editor/new') {
      return `/app?gameId=${lastGameId}`;
    }
    
    // אחרת, קישור רגיל למשחק חדש
    return '/app';
  };

  const handleLogout = useCallback(async () => {
    try {
      // Ensure logout function exists
      if (logout && typeof logout === 'function') {
        await logout();
      } else {
        console.warn('Logout function not available, clearing auth manually');
        // Manual cleanup if logout function is not available
        localStorage.removeItem('token');
        delete axios.defaults.headers.common['Authorization'];
      }
      
      // ניקוי ה-localStorage בעת התנתקות
      localStorage.removeItem('dnd_last_game_id');
      setLastGameId(null);
      safeNavigate(navigate, '/login');
    } catch (error) {
      console.error('Logout failed:', error);
      // Force logout even if API call fails
      localStorage.removeItem('token');
      localStorage.removeItem('dnd_last_game_id');
      delete axios.defaults.headers.common['Authorization'];
      setLastGameId(null);
      safeNavigate(navigate, '/login');
    }
  }, [logout, navigate, setLastGameId]);

  const handleMyAccount = useCallback(() => {
    try {
      const url = 'https://dnd-map-explorer.com/my-account/';
      const newWindow = window.open(url, '_blank');
      
      // Fallback if popup is blocked
      if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
        console.warn('Popup blocked, redirecting in current window');
        window.location.href = url;
      }
    } catch (error) {
      console.error('Error opening My Account:', error);
      // Final fallback
      window.location.href = 'https://dnd-map-explorer.com/my-account/';
    }
  }, []);

  return (
    <header className="header">
      <div className="logo logo-title">
        <div className="logo-images">
          <img src="/logo-dnd-crossed-swords.png" alt="D&D Logo" className="header-logo-image" />
        </div>
        <ProtectedLink to="/" className="header-logo">D&D MAP EXPLORER</ProtectedLink>
      </div>
      <nav className="nav">
        {currentUser ? (
          <>
            <ProtectedLink to={getMapEditorLink()}>Map Editor</ProtectedLink>
            <ProtectedLink to="/profile">Profile</ProtectedLink>
            <ProtectedLink to="/gallery">Gallery</ProtectedLink>
            <ProtectedLink to="/feedback">Feedback</ProtectedLink>
            <EnhancedButton onClick={handleMyAccount} className="logout-btn">My Account</EnhancedButton>
            <EnhancedButton onClick={handleLogout} className="logout-btn">Logout</EnhancedButton>
            {/* כפתור בדיקת בריאות - רק בסביבת פיתוח */}
            {process.env.NODE_ENV === 'development' && (
              <>
                <EnhancedButton 
                  onClick={() => {
                    const result = manualHealthCheck();
                    const navHistory = getNavigationHistory();
                    const historyText = navHistory.length > 0 
                      ? navHistory.map(nav => `${nav.target} (${new Date(nav.timestamp).toLocaleTimeString()})`).join('\n')
                      : 'No recent navigations';
                    
                    alert(`Button Health Check:\n${result.healthy}/${result.total} buttons are healthy\n\nNavigation History:\n${historyText}`);
                  }} 
                  className="logout-btn" 
                  style={{ fontSize: '12px', padding: '4px 8px' }}
                  title="Check button & navigation health (dev only)"
                >
                  🔧
                </EnhancedButton>
                <EnhancedButton 
                  onClick={() => {
                    clearNavigationHistory();
                    alert('Navigation history cleared!');
                  }} 
                  className="logout-btn" 
                  style={{ fontSize: '12px', padding: '4px 8px' }}
                  title="Clear navigation history (dev only)"
                >
                  🗑️
                </EnhancedButton>
              </>
            )}
          </>
        ) : (
          <Link to="/login">Login</Link>
        )}
      </nav>
    </header>
  );
};

export default Header; 